'use client';
import * as React from 'react';
import Link from 'next/link';
import { 
  Home, 
  Landmark, 
  DollarSign, 
  Heart, 
  Menu, 
  Cpu, 
  Info, 
  FaXTwitter, 
  FaFlipboard, 
  BsFacebook, 
  LuBriefcaseBusiness,
  Trophy,
  ShieldAlert,
  GraduationCap,
  Leaf,
  Globe,
  Gavel,
  Palette
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import { usePathname } from 'next/navigation';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Logo } from './Logo';
import { ScrollArea } from '@/components/ui/scroll-area';

function CurrentDate() {
  const [date, setDate] = React.useState<string | null>(null);

  React.useEffect(() => {
    const today = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    setDate(today.toLocaleDateString('en-US', options));
  }, []);

  if (!date) return <span className="text-[11px] text-[hsl(var(--section-header))] font-bold mt-1 invisible">Loading...</span>;

  return (
    <span className="text-[11px] text-[hsl(var(--section-header))] font-bold mt-1">
      {date}
    </span>
  );
}

const navItems = [
  { id: 'home', name: 'Home', icon: Home, iconComponent: Home, iconName: 'Home', href: '/', color: 'currentColor', desktop: true },
  { id: 'Politics', name: 'Politics', icon: Landmark, iconComponent: Landmark, iconName: 'Landmark', href: '/politics', color: '#ff4d5a', desktop: true },
  { id: 'Economy', name: 'Economy', icon: DollarSign, iconComponent: DollarSign, iconName: 'DollarSign', href: '/economy', color: '#60a5fa', desktop: true },
  { id: 'Business', name: 'Business', icon: LuBriefcaseBusiness, iconComponent: LuBriefcaseBusiness, iconName: 'LuBriefcaseBusiness', href: '/business', color: '#3b82f6', desktop: true },
  { id: 'Technology', name: 'Technology', icon: Cpu, iconComponent: Cpu, iconName: 'Cpu', href: '/technology', color: '#a78bfa', desktop: true },
  { id: 'Lifestyle', name: 'Lifestyle', icon: Palette, iconComponent: Palette, iconName: 'Palette', href: '/lifestyle', color: '#34d399', desktop: true },
  { id: 'Sports', name: 'Sports', icon: Trophy, iconComponent: Trophy, iconName: 'Trophy', href: '/sports', color: '#fb7185', desktop: false },
  { id: 'Crime', name: 'Crime', icon: Gavel, iconComponent: Gavel, iconName: 'Gavel', href: '/crime', color: '#ef4444', desktop: false },
  { id: 'World', name: 'World', icon: Globe, iconComponent: Globe, iconName: 'Globe', href: '/world', color: '#fbbf24', desktop: false },
  { id: 'Security', name: 'Security', icon: ShieldAlert, iconComponent: ShieldAlert, iconName: 'ShieldAlert', href: '/security-conflict', color: '#f43f5e', desktop: false },
  { id: 'Education', name: 'Education', icon: GraduationCap, iconComponent: GraduationCap, iconName: 'GraduationCap', href: '/education', color: '#38bdf8', desktop: false },
  { id: 'Environment', name: 'Environment', icon: Leaf, iconComponent: Leaf, iconName: 'Leaf', href: '/environment', color: '#22c55e', desktop: false },
  { id: 'About', name: 'About', icon: Info, iconComponent: Info, iconName: 'Info', href: '/about-us', color: 'currentColor', desktop: true },
];

export function Header() {
  const pathname = usePathname();
  
  const [isOpen, setIsOpen] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [lastScrollY, setLastScrollY] = React.useState(0);

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const getIsActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const desktopNavItems = navItems.filter(item => item.desktop);

  return (
    <>
      <header className={cn(
        "sticky top-0 z-40 w-full bg-background text-foreground transition-transform duration-300",
        !isVisible && "-translate-y-full"
      )}>
        <div className="w-full px-4 md:px-8 flex h-16 items-center md:h-24">
          
          {/* Section 1: Logo (Left) */}
          <div className="flex flex-1 items-center justify-start">
            <Link href="/" aria-label="News Afrika Home" className="flex items-center shrink-0">
              <Logo className="h-8 md:h-12 w-auto" />
            </Link>
          </div>

          {/* Section 2: Nav (Center - Desktop Only) */}
          <div className="hidden md:flex flex-[3] items-center justify-center">
            <nav className="flex items-center gap-6 lg:gap-8">
              {desktopNavItems.map((item) => {
                const active = getIsActive(item.href);
                const Icon = item.iconComponent;
                
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    className="flex items-center gap-2.5 text-xs font-bold transition-all relative group h-full py-4 text-foreground hover:text-primary"
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="whitespace-nowrap">{item.name}</span>
                    <span 
                      className={cn(
                        "absolute -bottom-1 left-0 h-[3px] transition-all duration-300",
                        active ? "w-full" : "w-0 group-hover:w-full"
                      )}
                      style={{ backgroundColor: item.color === 'currentColor' ? 'hsl(var(--primary))' : item.color }}
                    ></span>
                  </Link>
                );
              })}
            </nav>
          </div>
          
          {/* Section 3: Socials/Menu (Right) */}
          <div className="flex flex-1 items-center justify-end">
            {/* Desktop Registry */}
            <div className="hidden md:flex flex-col items-end">
              <div className="flex items-center space-x-3 mb-1">
                <Link href="https://x.com/newsafrika24" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="text-foreground hover:text-primary transition-colors">
                  <FaXTwitter className="h-4 w-4" />
                </Link>
                <Link href="https://www.facebook.com/newsafrika24" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-foreground hover:text-primary transition-colors">
                  <BsFacebook className="h-4 w-4" />
                </Link>
                <Link href="https://flipboard.com/@newsafrika24" target="_blank" rel="noopener noreferrer" aria-label="Flipboard" className="text-foreground hover:text-primary transition-colors">
                  <FaFlipboard className="h-4 w-4" />
                </Link>
              </div>
              <CurrentDate />
            </div>

            {/* Mobile Navigation Trigger */}
            <div className="md:hidden">
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Toggle menu" className="text-foreground hover:bg-foreground/10">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] bg-background p-0 flex flex-col">
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                  <ScrollArea className="flex-grow">
                    <nav className="flex flex-col py-8">
                      {navItems.map((item) => {
                        const active = getIsActive(item.href);
                        const Icon = item.iconComponent;
                        return (
                          <Link
                            key={item.id}
                            href={item.href}
                            onClick={() => setIsOpen(false)}
                            className={cn(
                              "flex items-center gap-4 px-6 py-4 text-lg font-bold transition-all text-foreground",
                              active && "bg-accent"
                            )}
                          >
                            <Icon className="h-6 w-6" style={{ color: item.color === 'currentColor' ? 'hsl(var(--foreground))' : item.color }} />
                            <span style={active ? { color: item.color === 'currentColor' ? 'hsl(var(--primary))' : item.color } : {}}>{item.name}</span>
                          </Link>
                        );
                      })}
                    </nav>
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
